import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { EditorContent, useEditor, type Editor, type JSONContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Baseline,
  Bold,
  CheckSquare,
  ChevronDown,
  Code,
  Code2,
  Columns3,
  Copy,
  Heading,
  Highlighter,
  Rows3,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Plus,
  Quote,
  Redo2,
  Smile,
  StickyNote,
  Strikethrough,
  Table as TableIcon,
  Trash2,
  Underline as UnderlineIcon,
  Undo2,
  Video as VideoIcon,
} from 'lucide-react';

import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { BlockHandles, LinkPreview } from './blockControls';
import { buildCompactExtensions, buildExtensions } from './extensions';
import { pickImageFile, type MentionItem } from './suggestion';
import type { EditorPageContent, PageProvider } from './pageEmbed';

// Prose + node styling (tables, callouts, checklists, code tokens, mentions).
const PROSE =
  'max-w-none text-sm focus:outline-none ' +
  '[&_h1]:mt-3 [&_h1]:text-2xl [&_h1]:font-semibold [&_h2]:mt-3 [&_h2]:text-xl [&_h2]:font-semibold [&_h3]:mt-2 [&_h3]:text-lg [&_h3]:font-semibold ' +
  '[&_h4]:mt-2 [&_h4]:text-base [&_h4]:font-semibold [&_h5]:mt-2 [&_h5]:text-sm [&_h5]:font-semibold [&_h6]:mt-2 [&_h6]:text-sm [&_h6]:font-semibold [&_h6]:text-muted-foreground ' +
  // Video embed (responsive 16:9)
  '[&_div[data-type=video]]:relative [&_div[data-type=video]]:my-2 [&_div[data-type=video]]:aspect-video [&_div[data-type=video]]:overflow-hidden [&_div[data-type=video]]:rounded-md [&_div[data-type=video]_iframe]:absolute [&_div[data-type=video]_iframe]:inset-0 [&_div[data-type=video]_iframe]:size-full ' +
  '[&_p]:my-1.5 [&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5 ' +
  // Ordered-list markers honor the `type` attribute (a/A/i/I) — decimal default.
  '[&_ol[type=a]]:list-[lower-alpha] [&_ol[type=A]]:list-[upper-alpha] [&_ol[type=i]]:list-[lower-roman] [&_ol[type=I]]:list-[upper-roman] ' +
  '[&_a]:text-primary [&_a]:underline [&_hr]:my-4 [&_hr]:border-t ' +
  '[&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground ' +
  // Task list (checklists)
  '[&_ul[data-type=taskList]]:list-none [&_ul[data-type=taskList]]:pl-1 ' +
  '[&_ul[data-type=taskList]_li]:flex [&_ul[data-type=taskList]_li]:items-start [&_ul[data-type=taskList]_li]:gap-2 ' +
  '[&_ul[data-type=taskList]_li>label]:mt-1 ' +
  // Callout
  '[&_div[data-type=callout]]:my-2 [&_div[data-type=callout]]:rounded-md [&_div[data-type=callout]]:border [&_div[data-type=callout]]:border-amber-300 [&_div[data-type=callout]]:bg-amber-50 [&_div[data-type=callout]]:p-3 ' +
  'dark:[&_div[data-type=callout]]:border-amber-900 dark:[&_div[data-type=callout]]:bg-amber-950/30 ' +
  // Tables — `table-fixed` so dragged column widths stick; the wrapper scrolls
  // horizontally once a table is made wider than the page.
  '[&_.tableWrapper]:my-2 [&_.tableWrapper]:overflow-x-auto ' +
  '[&_table]:my-2 [&_table]:w-full [&_table]:table-fixed [&_table]:border-collapse [&_table]:text-sm ' +
  '[&_th]:relative [&_th]:min-w-12 [&_th]:border [&_th]:bg-muted [&_th]:p-1.5 [&_th]:text-left ' +
  '[&_td]:relative [&_td]:min-w-12 [&_td]:border [&_td]:p-1.5 ' +
  // Column-resize handle (grab the right border of any cell to drag).
  '[&_.column-resize-handle]:absolute [&_.column-resize-handle]:-right-0.5 [&_.column-resize-handle]:top-0 [&_.column-resize-handle]:bottom-0 [&_.column-resize-handle]:w-1 [&_.column-resize-handle]:bg-primary/60 ' +
  '[&_.resize-cursor]:cursor-col-resize ' +
  // Code
  '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_pre]:my-2 [&_pre]:rounded-md [&_pre]:bg-zinc-900 [&_pre]:p-3 [&_pre]:text-zinc-100 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-zinc-100 ' +
  '[&_.hljs-keyword]:text-purple-400 [&_.hljs-string]:text-emerald-400 [&_.hljs-comment]:text-zinc-500 [&_.hljs-number]:text-amber-300 [&_.hljs-title]:text-sky-400 [&_.hljs-attr]:text-sky-300 [&_.hljs-built_in]:text-sky-400 ' +
  // Mentions
  '[&_.mention]:rounded [&_.mention]:bg-primary/10 [&_.mention]:px-1 [&_.mention]:text-primary [&_.mention]:font-medium ' +
  // Placeholder
  '[&_.is-editor-empty:first-child]:before:text-muted-foreground [&_.is-editor-empty:first-child]:before:content-[attr(data-placeholder)] [&_.is-editor-empty:first-child]:before:pointer-events-none [&_.is-editor-empty:first-child]:before:float-left [&_.is-editor-empty:first-child]:before:h-0';

const HIGHLIGHTS = ['#fde68a', '#bbf7d0', '#bfdbfe', '#fbcfe8', '#e9d5ff'];
const COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#111827'];

export interface BlockEditorValue {
  json: JSONContent;
  html: string;
  text: string;
}

/** Imperative handle so a host can force a save (manual "Save" / "Done"). */
export interface BlockEditorHandle {
  /** Persist any pending edits now; no-op when the document is unchanged. */
  flush: () => void;
}

/** Render a stored page document read-only — used by the page-embed block to
 *  transclude another page. No provider is threaded down, so embeds nested in
 *  transcluded content degrade to static chips (no recursion / refetch). */
function renderPageContent(page: EditorPageContent) {
  return <BlockContent value={page.body_json ?? null} html={page.body} />;
}

interface BlockEditorProps {
  /** Initial document (ProseMirror JSON). Falls back to `html` for legacy pages. */
  value?: JSONContent | null;
  html?: string;
  placeholder?: string;
  members?: MentionItem[];
  /** Enables the "/page" embed block (FE-ADR-055). */
  pageProvider?: PageProvider;
  /** Compact preset (work-item descriptions): trimmed tools + slim toolbar. */
  compact?: boolean;
  /** Upload a pasted/picked/dropped image; returns the URL to embed. Enables
   *  paste + drag-drop image handling. */
  uploadImage?: (file: File) => Promise<string>;
  /** Debounced while typing + flushed on blur / imperative flush(). */
  onSave: (value: BlockEditorValue) => void;
  /** Fired on every edit (before the debounce) so hosts can track "unsaved". */
  onChange?: () => void;
}

const countWords = (text: string) => {
  const t = text.trim();
  return t ? t.split(/\s+/).length : 0;
};

// Autosave fires this long after the last keystroke. Deliberately long to keep
// write volume low; edits are never lost on exit — flushed on blur, manual Save,
// Done, and unmount, with a beforeunload guard while dirty.
const AUTOSAVE_DEBOUNCE_MS = 10_000;

export const BlockEditor = forwardRef<BlockEditorHandle, BlockEditorProps>(function BlockEditor(
  { value, html, placeholder, members, pageProvider, compact, uploadImage, onSave, onChange },
  ref,
) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Unsaved-edits flag: set on every change, cleared once flushed — lets flush()
  // no-op on a clean document (so manual Save / blur never re-write unchanged text).
  const dirty = useRef(false);
  // Set in onCreate so the paste/drop handlers (defined at init) can reach the
  // editor to insert uploaded images.
  const editorRef = useRef<Editor | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [words, setWords] = useState(0);

  const flush = useCallback(
    (e: Editor) => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
      if (!dirty.current) return;
      dirty.current = false;
      onSave({ json: e.getJSON(), html: e.isEmpty ? '' : e.getHTML(), text: e.getText() });
    },
    [onSave],
  );

  const insertImageFiles = useCallback(
    (files: File[], pos?: number) => {
      const images = files.filter((f) => f.type.startsWith('image/'));
      if (!uploadImage || !images.length) return false;
      images.forEach((file) => {
        void uploadImage(file)
          .then((url) => {
            const e = editorRef.current;
            if (!url || !e || e.isDestroyed) return;
            // `.then` runs post-render with focus still on the editor (no rAF —
            // that's throttled in background tabs). `setImage` inserts at the
            // selection and handles block placement; for a drop, move the
            // selection to the drop point first.
            const chain = e.chain().focus();
            if (typeof pos === 'number') chain.setTextSelection(pos);
            chain.setImage({ src: url }).run();
          })
          .catch((err) => {
            console.error('[inline-image] upload failed', err);
            toast.error('Could not upload image');
          });
      });
      return true;
    },
    [uploadImage],
  );

  const editor = useEditor({
    extensions: compact
      ? buildCompactExtensions({ placeholder })
      : buildExtensions({ placeholder, members, pageProvider, renderContent: renderPageContent }),
    content: value && Object.keys(value).length ? value : html || '',
    editorProps: {
      attributes: { class: PROSE },
      handlePaste: (_view, event) => {
        const dt = event.clipboardData;
        const files = Array.from(dt?.files ?? []);
        // Screenshots and "copy image" from a browser expose the image via
        // clipboard *items* (a blob), not files — pull those in too.
        const fromItems = Array.from(dt?.items ?? [])
          .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
          .map((it) => it.getAsFile())
          .filter((f): f is File => f != null);
        const images = files.length ? files : fromItems;
        if (!insertImageFiles(images)) return false;
        event.preventDefault();
        return true;
      },
      handleDrop: (view, event, _slice, moved) => {
        if (moved) return false;
        const drag = event as DragEvent;
        const files = Array.from(drag.dataTransfer?.files ?? []);
        if (!files.some((f) => f.type.startsWith('image/'))) return false;
        const coords = view.posAtCoords({ left: drag.clientX, top: drag.clientY });
        if (!insertImageFiles(files, coords?.pos)) return false;
        event.preventDefault();
        return true;
      },
    },
    onCreate: ({ editor: e }) => {
      setWords(countWords(e.getText()));
    },
    onBlur: ({ editor: e }) => flush(e),
    onUpdate: ({ editor: e }) => {
      setWords(countWords(e.getText()));
      dirty.current = true;
      onChange?.();
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => flush(e), AUTOSAVE_DEBOUNCE_MS);
    },
  });

  // Track the *live* editor. onCreate would capture the instance React
  // StrictMode discards on its double-mount, leaving a destroyed editor in the
  // ref — so the async image insert (which reads editorRef in a promise) must
  // always see the current one.
  useEffect(() => {
    editorRef.current = editor ?? null;
  }, [editor]);

  useImperativeHandle(ref, () => ({ flush: () => editor && flush(editor) }), [editor, flush]);

  // On unmount (route change / Done), persist any edit still waiting on the
  // debounce so it isn't lost. Runs before useEditor tears the editor down.
  useEffect(
    () => () => {
      if (editor && dirty.current) flush(editor);
      if (timer.current) clearTimeout(timer.current);
    },
    [editor, flush],
  );

  if (!editor) return null;

  return (
    <div className="space-y-2">
      {compact ? (
        <CompactToolbar editor={editor} uploadImage={uploadImage} />
      ) : (
        <Toolbar editor={editor} />
      )}
      {/* Slash menu (“/”) inserts blocks; this bubble menu formats the selection.
          Hidden inside tables — the table menu takes over there. */}
      <BubbleMenu
        editor={editor}
        pluginKey="textBubbleMenu"
        shouldShow={({ editor: e, state }) => e.isFocused && !state.selection.empty && !e.isActive('table')}
        className="bg-popover flex items-center gap-0.5 rounded-md border p-1 shadow-md"
      >
        <Btn editor={editor} active="bold" onClick={() => editor.chain().focus().toggleBold().run()} icon={Bold} label="Bold" />
        <Btn editor={editor} active="italic" onClick={() => editor.chain().focus().toggleItalic().run()} icon={Italic} label="Italic" />
        <Btn editor={editor} active="underline" onClick={() => editor.chain().focus().toggleUnderline().run()} icon={UnderlineIcon} label="Underline" />
        <Btn editor={editor} active="strike" onClick={() => editor.chain().focus().toggleStrike().run()} icon={Strikethrough} label="Strikethrough" />
        <Btn editor={editor} active="code" onClick={() => editor.chain().focus().toggleCode().run()} icon={Code} label="Code" />
        <LinkButton editor={editor} />
        {/* Colour + highlight need the TextStyle/Color/Highlight extensions,
            which the compact preset omits — hide them there. */}
        {!compact ? (
          <>
            <Swatches title="Text color" colors={COLORS} onPick={(c) => editor.chain().focus().setColor(c).run()} icon={Bold} swatchLabel="A" />
            <Swatches title="Highlight" colors={HIGHLIGHTS} onPick={(c) => editor.chain().focus().toggleHighlight({ color: c }).run()} icon={Highlighter} />
          </>
        ) : null}
      </BubbleMenu>
      {/* Table controls — appear whenever the cursor is inside a table. */}
      <TableBubbleMenu editor={editor} />
      {/* Hover any link to preview its target. */}
      <LinkPreview editor={editor} />
      {/* Full-width document. `pl-12` opens a left gutter wide enough to fully
          contain the per-block drag/insert handle (the plugin hangs it flush
          off the block's left edge); `relative` anchors the handle's portal.
          The compact preset drops the handles + gutter + block context menu —
          a task note doesn't need block reordering chrome. */}
      <div
        className={cn('relative w-full pt-[15px]', compact ? '' : 'pl-12')}
        onContextMenu={
          compact
            ? undefined
            : (e) => {
                e.preventDefault();
                const pos = editor.view.posAtCoords({ left: e.clientX, top: e.clientY });
                if (pos) editor.commands.setTextSelection(pos.pos);
                setMenu({ x: e.clientX, y: e.clientY });
              }
        }
      >
        {compact ? null : <BlockHandles editor={editor} />}
        <EditorContent editor={editor} />
      </div>
      {!compact && words > 0 ? (
        <div className="text-muted-foreground border-t pt-2 pl-1 text-xs">
          {words.toLocaleString()} {words === 1 ? 'word' : 'words'} ·{' '}
          {Math.max(1, Math.ceil(words / 200))} min read
        </div>
      ) : null}
      {menu ? (
        <BlockContextMenu editor={editor} x={menu.x} y={menu.y} onClose={() => setMenu(null)} />
      ) : null}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Block context menu (Plane-style: Copy link / Duplicate / Delete)
// ---------------------------------------------------------------------------
function currentBlock(editor: Editor) {
  const { $from } = editor.state.selection;
  const depth = Math.min(1, $from.depth); // top-level block
  return { node: $from.node(depth), from: $from.before(depth), to: $from.after(depth) };
}

function BlockContextMenu({
  editor,
  x,
  y,
  onClose,
}: {
  editor: Editor;
  x: number;
  y: number;
  onClose: () => void;
}) {
  const item = 'hover:bg-muted flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm';
  return (
    <>
      <button
        type="button"
        aria-label="Close menu"
        className="fixed inset-0 z-50 cursor-default"
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div className="bg-popover text-popover-foreground fixed z-50 w-44 rounded-md border p-1 shadow-md" style={{ left: x, top: y }}>
        <button
          type="button"
          className={item}
          onClick={() => {
            void navigator.clipboard?.writeText(window.location.href);
            onClose();
          }}
        >
          <Link2 className="size-4" /> Copy page link
        </button>
        <button
          type="button"
          className={item}
          onClick={() => {
            const { node, to } = currentBlock(editor);
            editor.chain().focus().insertContentAt(to, node.toJSON()).run();
            onClose();
          }}
        >
          <Copy className="size-4" /> Duplicate
        </button>
        <button
          type="button"
          className={cn(item, 'text-destructive')}
          onClick={() => {
            const { from, to } = currentBlock(editor);
            editor.chain().focus().deleteRange({ from, to }).run();
            onClose();
          }}
        >
          <Trash2 className="size-4" /> Delete
        </button>
      </div>
    </>
  );
}

/** Read-only render of a stored page document (locked/archived view). When a
 *  `pageProvider` is supplied, embedded page blocks resolve (cards/transclusion);
 *  without it they fall back to static chips. */
export function BlockContent({
  value,
  html,
  pageProvider,
}: {
  value?: JSONContent | null;
  html?: string;
  pageProvider?: PageProvider;
}) {
  const editor = useEditor({
    extensions: buildExtensions({
      pageProvider,
      renderContent: pageProvider ? renderPageContent : undefined,
    }),
    content: value && Object.keys(value).length ? value : html || '',
    editable: false,
    editorProps: { attributes: { class: cn(PROSE, 'min-h-0') } },
  });
  if (!editor) return null;
  return <EditorContent editor={editor} />;
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------
const BTN = 'rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40';
const ACTIVE = 'bg-muted text-foreground';

function Btn({
  editor,
  icon: Icon,
  label,
  onClick,
  active,
  disabled,
}: {
  editor: Editor;
  icon: typeof Bold;
  label: string;
  onClick: () => void;
  active?: string;
  disabled?: boolean;
}) {
  const isActive = active ? editor.isActive(active) : false;
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={isActive}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(BTN, isActive && ACTIVE)}
    >
      <Icon className="size-4" />
    </button>
  );
}

/** Inline +/− button for the table menu (text glyph, not an icon). */
function TableStep({
  editor,
  label,
  glyph,
  onClick,
}: {
  editor: Editor;
  label: string;
  glyph: '+' | '−';
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={!editor.isActive('table')}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(BTN, 'w-7 text-sm leading-none font-medium')}
    >
      {glyph}
    </button>
  );
}

/** Row/column controls — shown whenever the cursor sits inside a table. */
function TableBubbleMenu({ editor }: { editor: Editor }) {
  const chain = () => editor.chain().focus();
  const sep = <span className="bg-border mx-0.5 h-5 w-px shrink-0" aria-hidden />;
  const groupLabel = 'text-muted-foreground flex items-center gap-1 px-1 text-xs';
  return (
    <BubbleMenu
      editor={editor}
      pluginKey="tableBubbleMenu"
      // Require focus too — a custom shouldShow replaces TipTap's default focus
      // check, so without this the controls show on load when the cursor
      // defaults inside a table (before the user interacts).
      shouldShow={({ editor: e }) => e.isFocused && e.isActive('table')}
      className="bg-popover flex items-center gap-0.5 rounded-md border p-1 shadow-md"
    >
      <span className={groupLabel}>
        <Rows3 className="size-3.5" /> Row
      </span>
      <TableStep editor={editor} glyph="+" label="Add row below" onClick={() => chain().addRowAfter().run()} />
      <TableStep editor={editor} glyph="−" label="Delete row" onClick={() => chain().deleteRow().run()} />
      {sep}
      <span className={groupLabel}>
        <Columns3 className="size-3.5" /> Col
      </span>
      <TableStep editor={editor} glyph="+" label="Add column right" onClick={() => chain().addColumnAfter().run()} />
      <TableStep editor={editor} glyph="−" label="Delete column" onClick={() => chain().deleteColumn().run()} />
      {sep}
      <Btn editor={editor} icon={Heading} label="Toggle header row" onClick={() => chain().toggleHeaderRow().run()} />
      <Btn editor={editor} icon={Trash2} label="Delete table" onClick={() => chain().deleteTable().run()} />
    </BubbleMenu>
  );
}

function Swatches({
  title,
  colors,
  onPick,
  icon: Icon,
  swatchLabel,
}: {
  title: string;
  colors: string[];
  onPick: (c: string) => void;
  icon: typeof Bold;
  swatchLabel?: string;
}) {
  return (
    <div className="group relative">
      <button type="button" aria-label={title} title={title} className={BTN} onMouseDown={(e) => e.preventDefault()}>
        {swatchLabel ? <span className="text-sm font-semibold">{swatchLabel}</span> : <Icon className="size-4" />}
      </button>
      <div className="bg-popover absolute left-0 z-10 hidden grid-cols-6 gap-1 rounded-md border p-1.5 shadow-md group-hover:grid">
        {colors.map((c) => (
          <button
            key={c}
            type="button"
            aria-label={c}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(c)}
            className="size-5 rounded"
            style={{ backgroundColor: c }}
          />
        ))}
      </div>
    </div>
  );
}

function LinkButton({ editor }: { editor: Editor }) {
  return (
    <Btn
      editor={editor}
      icon={Link2}
      label="Link"
      active="link"
      onClick={() => {
        const prev = (editor.getAttributes('link').href as string) ?? '';
        const url = window.prompt('Link URL', prev);
        if (url === null) return;
        if (url === '') editor.chain().focus().extendMarkRange('link').unsetLink().run();
        else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
      }}
    />
  );
}

function Sep() {
  return <span className="bg-border mx-0.5 h-5 w-px shrink-0" aria-hidden />;
}

// ---------------------------------------------------------------------------
// Fixed toolbar (Plane-style): block-type dropdown + color + inline + blocks
// ---------------------------------------------------------------------------
const TEXT_COLORS: { label: string; value: string | null; swatch: string }[] = [
  { label: 'Default', value: null, swatch: 'currentColor' },
  { label: 'Gray', value: '#6b7280', swatch: '#6b7280' },
  { label: 'Peach', value: '#ef4444', swatch: '#ef4444' },
  { label: 'Pink', value: '#ec4899', swatch: '#ec4899' },
  { label: 'Orange', value: '#f59e0b', swatch: '#f59e0b' },
  { label: 'Green', value: '#10b981', swatch: '#10b981' },
  { label: 'Light blue', value: '#38bdf8', swatch: '#38bdf8' },
  { label: 'Dark blue', value: '#2563eb', swatch: '#2563eb' },
];

const EMOJIS = '😀 😄 😁 😅 😂 🙂 😉 😍 🤩 🤔 😎 😴 🙌 👍 👎 👏 🙏 💪 🔥 ✨ ⭐ 🎉 ✅ ❌ ⚠️ ❤️ 💙 💚 💜 🚀 🐛 🛠️ 📌 📎 📊 📈 💡 🧠 ⏰ 📅'.split(' ');

interface BlockType {
  label: string;
  isActive: (e: Editor) => boolean;
  run: (e: Editor) => void;
}

const BLOCK_TYPES: BlockType[] = [
  { label: 'Text', isActive: (e) => e.isActive('paragraph') && !e.isActive('bulletList') && !e.isActive('orderedList'), run: (e) => e.chain().focus().setParagraph().run() },
  ...[1, 2, 3, 4, 5, 6].map((level) => ({
    label: `Heading ${level}`,
    isActive: (e: Editor) => e.isActive('heading', { level }),
    run: (e: Editor) => e.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 | 4 | 5 | 6 }).run(),
  })),
  { label: 'Bulleted list', isActive: (e) => e.isActive('bulletList'), run: (e) => e.chain().focus().toggleBulletList().run() },
  { label: 'Numbered list', isActive: (e) => e.isActive('orderedList'), run: (e) => e.chain().focus().toggleOrderedList().run() },
  { label: 'To-do list', isActive: (e) => e.isActive('taskList'), run: (e) => e.chain().focus().toggleTaskList().run() },
  { label: 'Quote', isActive: (e) => e.isActive('blockquote'), run: (e) => e.chain().focus().toggleBlockquote().run() },
  { label: 'Code block', isActive: (e) => e.isActive('codeBlock'), run: (e) => e.chain().focus().toggleCodeBlock().run() },
  { label: 'Callout', isActive: (e) => e.isActive('callout'), run: (e) => e.chain().focus().toggleCallout().run() },
];

function Toolbar({ editor }: { editor: Editor }) {
  const current = BLOCK_TYPES.find((b) => b.isActive(editor)) ?? BLOCK_TYPES[0];
  return (
    <div className="bg-background sticky top-0 z-10 flex flex-wrap items-center gap-0.5 rounded-md border p-1">
      {/* Block type */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 gap-1 px-2 font-normal">
            {current.label}
            <ChevronDown className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-44">
          {BLOCK_TYPES.map((b) => (
            <DropdownMenuItem key={b.label} onClick={() => b.run(editor)}>
              {b.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Sep />

      {/* Color */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" aria-label="Text color" title="Text color" className={BTN} onMouseDown={(e) => e.preventDefault()}>
            <Baseline className="size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-40">
          {TEXT_COLORS.map((c) => (
            <DropdownMenuItem
              key={c.label}
              onClick={() => (c.value ? editor.chain().focus().setColor(c.value).run() : editor.chain().focus().unsetColor().run())}
            >
              <span className="grid size-4 place-items-center rounded border text-xs font-semibold" style={{ color: c.swatch }}>A</span>
              {c.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Sep />

      <Btn editor={editor} icon={Bold} label="Bold" active="bold" onClick={() => editor.chain().focus().toggleBold().run()} />
      <Btn editor={editor} icon={Italic} label="Italic" active="italic" onClick={() => editor.chain().focus().toggleItalic().run()} />
      <Btn editor={editor} icon={UnderlineIcon} label="Underline" active="underline" onClick={() => editor.chain().focus().toggleUnderline().run()} />
      <Btn editor={editor} icon={Strikethrough} label="Strikethrough" active="strike" onClick={() => editor.chain().focus().toggleStrike().run()} />
      <Btn editor={editor} icon={Highlighter} label="Highlight" active="highlight" onClick={() => editor.chain().focus().toggleHighlight().run()} />
      <Sep />

      <AlignMenu editor={editor} />
      <Sep />

      <OrderedListMenu editor={editor} />
      <Btn editor={editor} icon={List} label="Bulleted list" active="bulletList" onClick={() => editor.chain().focus().toggleBulletList().run()} />
      <Btn editor={editor} icon={CheckSquare} label="To-do list" active="taskList" onClick={() => editor.chain().focus().toggleTaskList().run()} />
      <Sep />

      <LinkButton editor={editor} />
      <InsertMenu editor={editor} />
      <EmojiButton editor={editor} />
      <Sep />

      <Btn editor={editor} icon={Undo2} label="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()} />
      <Btn editor={editor} icon={Redo2} label="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()} />
    </div>
  );
}

// Compact toolbar (work-item descriptions): block type + core inline marks +
// link + image upload + undo/redo. No colour/align/table/video/callout/emoji —
// the slash menu covers block inserts.
const COMPACT_BLOCK_LABELS = new Set([
  'Text',
  'Heading 1',
  'Heading 2',
  'Heading 3',
  'Bulleted list',
  'Numbered list',
  'To-do list',
  'Quote',
  'Code block',
]);
const COMPACT_BLOCK_TYPES = BLOCK_TYPES.filter((b) => COMPACT_BLOCK_LABELS.has(b.label));

function ImageButton({
  editor,
  uploadImage,
}: {
  editor: Editor;
  uploadImage?: (file: File) => Promise<string>;
}) {
  return (
    <Btn
      editor={editor}
      icon={ImageIcon}
      label="Image"
      onClick={() => {
        if (uploadImage) {
          void pickImageFile().then((file) => {
            if (!file) return;
            void uploadImage(file)
              .then((url) => {
                if (url && !editor.isDestroyed) editor.chain().focus().setImage({ src: url }).run();
              })
              .catch((err) => {
                console.error('[inline-image] upload/insert failed', err);
                toast.error('Could not upload image');
              });
          });
          return;
        }
        const url = window.prompt('Image URL');
        if (url) editor.chain().focus().setImage({ src: url }).run();
      }}
    />
  );
}

function CompactToolbar({
  editor,
  uploadImage,
}: {
  editor: Editor;
  uploadImage?: (file: File) => Promise<string>;
}) {
  const current = COMPACT_BLOCK_TYPES.find((b) => b.isActive(editor)) ?? COMPACT_BLOCK_TYPES[0];
  return (
    <div className="bg-background sticky top-0 z-10 flex flex-wrap items-center gap-0.5 rounded-md border p-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 gap-1 px-2 font-normal">
            {current.label}
            <ChevronDown className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-44">
          {COMPACT_BLOCK_TYPES.map((b) => (
            <DropdownMenuItem key={b.label} onClick={() => b.run(editor)}>
              {b.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Sep />
      <Btn editor={editor} icon={Bold} label="Bold" active="bold" onClick={() => editor.chain().focus().toggleBold().run()} />
      <Btn editor={editor} icon={Italic} label="Italic" active="italic" onClick={() => editor.chain().focus().toggleItalic().run()} />
      <Btn editor={editor} icon={UnderlineIcon} label="Underline" active="underline" onClick={() => editor.chain().focus().toggleUnderline().run()} />
      <Btn editor={editor} icon={Strikethrough} label="Strikethrough" active="strike" onClick={() => editor.chain().focus().toggleStrike().run()} />
      <Sep />
      <Btn editor={editor} icon={List} label="Bulleted list" active="bulletList" onClick={() => editor.chain().focus().toggleBulletList().run()} />
      <OrderedListMenu editor={editor} />
      <Btn editor={editor} icon={CheckSquare} label="To-do list" active="taskList" onClick={() => editor.chain().focus().toggleTaskList().run()} />
      <Sep />
      <LinkButton editor={editor} />
      <ImageButton editor={editor} uploadImage={uploadImage} />
      <Sep />
      <Btn editor={editor} icon={Undo2} label="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()} />
      <Btn editor={editor} icon={Redo2} label="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()} />
    </div>
  );
}

const ALIGN_OPTIONS = [
  { value: 'left', icon: AlignLeft, label: 'Align left' },
  { value: 'center', icon: AlignCenter, label: 'Align center' },
  { value: 'right', icon: AlignRight, label: 'Align right' },
] as const;

/** Alignment collapsed into a single dropdown (trigger shows the active one). */
function AlignMenu({ editor }: { editor: Editor }) {
  const current = ALIGN_OPTIONS.find((o) => editor.isActive({ textAlign: o.value })) ?? ALIGN_OPTIONS[0];
  const CurrentIcon = current.icon;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Text alignment"
          title="Text alignment"
          className={cn(BTN, 'flex items-center gap-0.5')}
          onMouseDown={(e) => e.preventDefault()}
        >
          <CurrentIcon className="size-4" />
          <ChevronDown className="size-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {ALIGN_OPTIONS.map((o) => {
          const OptIcon = o.icon;
          return (
            <DropdownMenuItem key={o.value} onClick={() => editor.chain().focus().setTextAlign(o.value).run()}>
              <OptIcon className="size-4" /> {o.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Ordered-list control: the icon toggles a numbered list; the caret picks the
 *  marker style — Numbers (1.), Letters (a.), or Roman (i.). Marker is stored as
 *  the ordered list's `type` attribute (persisted + rendered via `<ol type>`). */
const ORDERED_LIST_STYLES = [
  { type: '1', label: '1. Numbers' },
  { type: 'a', label: 'a. Letters' },
  { type: 'i', label: 'i. Roman' },
] as const;

function OrderedListMenu({ editor }: { editor: Editor }) {
  const setStyle = (type: string) => {
    const chain = editor.chain().focus();
    if (!editor.isActive('orderedList')) chain.toggleOrderedList();
    chain.updateAttributes('orderedList', { type }).run();
  };
  return (
    <div className="flex items-center">
      <Btn
        editor={editor}
        icon={ListOrdered}
        label="Numbered list"
        active="orderedList"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="List style"
            title="List style"
            className={cn(BTN, '-ml-1 px-0.5')}
            onMouseDown={(e) => e.preventDefault()}
          >
            <ChevronDown className="size-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-36">
          {ORDERED_LIST_STYLES.map((o) => (
            <DropdownMenuItem key={o.type} onClick={() => setStyle(o.type)}>
              {o.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** Block inserts grouped under one "Insert" dropdown — keeps the toolbar slim
 *  now that the slash menu and per-block "+" handle cover the same ground. */
function InsertMenu({ editor }: { editor: Editor }) {
  const chain = () => editor.chain().focus();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="text-muted-foreground h-8 gap-1 px-2 font-normal">
          <Plus className="size-4" /> Insert
          <ChevronDown className="size-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-44">
        <DropdownMenuItem onClick={() => chain().toggleBlockquote().run()}>
          <Quote className="size-4" /> Quote
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => chain().toggleCallout().run()}>
          <StickyNote className="size-4" /> Callout
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => chain().toggleCodeBlock().run()}>
          <Code2 className="size-4" /> Code block
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => chain().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
          <TableIcon className="size-4" /> Table
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            const url = window.prompt('Image URL');
            if (url) chain().setImage({ src: url }).run();
          }}
        >
          <ImageIcon className="size-4" /> Image
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            const url = window.prompt('Video URL (YouTube, Vimeo, or embed link)');
            if (url) chain().setVideo({ src: url }).run();
          }}
        >
          <VideoIcon className="size-4" /> Video
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => chain().setHorizontalRule().run()}>
          <Minus className="size-4" /> Divider
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function EmojiButton({ editor }: { editor: Editor }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label="Emoji" title="Emoji" className={BTN} onMouseDown={(e) => e.preventDefault()}>
          <Smile className="size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1">
        <div className="grid grid-cols-8 gap-0.5">
          {EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => editor.chain().focus().insertContent(emoji).run()}
              className="hover:bg-muted flex size-6 items-center justify-center rounded text-base"
            >
              {emoji}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
